// src/components/layout/Footer.jsx

import {
  memo,
  useCallback,
} from "react";

import {
  ActionIcon,
  Box,
  Container,
  Group,
  Image,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";

import { Link } from "react-router-dom";

import {
  FaArrowUp,
  FaHeart,
} from "react-icons/fa";

import {
  motion as Motion,
  useReducedMotion,
} from "framer-motion";

import Logo from "../../assets/FabianaTernoFooter.jpeg";

import {
  footerTexts,
} from "../utils/footerConstants";

import styles from "../../Styles/Footer.module.css";

/* ==========================================================================
   CONFIGURAÇÕES
   ========================================================================== */

const SMOOTH_EASE = [
  0.16,
  1,
  0.3,
  1,
];

/* ==========================================================================
   VARIANTES
   ========================================================================== */

const footerVariants = {
  hidden: {
    opacity: 0,
    y: 24,
  },

  visible: {
    opacity: 1,
    y: 0,

    transition: {
      duration: 0.8,
      ease: SMOOTH_EASE,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 14,
  },

  visible: {
    opacity: 1,
    y: 0,

    transition: {
      duration: 0.65,
      ease: SMOOTH_EASE,
    },
  },
};

/* ==========================================================================
   COMPONENTE PRINCIPAL
   ========================================================================== */

function Footer() {
  const currentYear =
    new Date().getFullYear();

  const prefersReducedMotion =
    useReducedMotion();

  const companyName =
    footerTexts?.companyName ??
    "Clínica de Estética Catarino";

  const description =
    footerTexts?.description ??
    "Estética avançada e atendimento humanizado.";

  const loveMessage =
    footerTexts?.loveMessage ??
    "Feito com carinho";

  const handleScrollToTop =
    useCallback(() => {
      if (
        typeof window === "undefined"
      ) {
        return;
      }

      window.scrollTo({
        top: 0,
        left: 0,
        behavior:
          prefersReducedMotion
            ? "auto"
            : "smooth",
      });
    }, [prefersReducedMotion]);

  return (
    <Motion.footer
      className={styles.footer}
      role="contentinfo"
      aria-label={`Rodapé da ${companyName}`}
      variants={
        prefersReducedMotion
          ? undefined
          : footerVariants
      }
      initial={
        prefersReducedMotion
          ? false
          : "hidden"
      }
      whileInView={
        prefersReducedMotion
          ? undefined
          : "visible"
      }
      viewport={{
        once: true,
        amount: 0.15,
      }}
    >
      {/* Fundo decorativo */}

      <Box
        className={
          styles.background
        }
        aria-hidden="true"
      >
        <span
          className={`${styles.orb} ${styles.orbLeft}`}
        />

        <span
          className={`${styles.orb} ${styles.orbRight}`}
        />
      </Box>

      {/* Conteúdo em largura total */}

      <Container
        fluid
        className={
          styles.container
        }
      >
        <Box
          className={
            styles.footerBar
          }
        >
          {/* Marca */}

          <Motion.div
            variants={
              prefersReducedMotion
                ? undefined
                : itemVariants
            }
            className={
              styles.brandArea
            }
          >
            <Link
              to="/"
              className={
                styles.brand
              }
              aria-label={`Ir para o início da ${companyName}`}
            >
              <Motion.div
                className={
                  styles.logoWrapper
                }
                whileHover={
                  prefersReducedMotion
                    ? undefined
                    : {
                        y: -2,
                        scale: 1.03,
                      }
                }
                whileTap={{
                  scale: 0.97,
                }}
                transition={{
                  duration: 0.4,
                  ease: SMOOTH_EASE,
                }}
              >
                <Image
                  src={Logo}
                  alt={companyName}
                  className={
                    styles.logo
                  }
                  loading="lazy"
                />
              </Motion.div>

              <Stack
                gap={2}
                className={
                  styles.brandText
                }
              >
                <Text
                  component="strong"
                  className={
                    styles.brandTitle
                  }
                >
                  {companyName}
                </Text>

                <Text
                  className={
                    styles.description
                  }
                >
                  {description}
                </Text>
              </Stack>
            </Link>
          </Motion.div>

          {/* Espaço visual central */}

          <Box
            className={
              styles.footerSpacer
            }
            aria-hidden="true"
          />

          {/* Copyright */}

          <Motion.div
            variants={
              prefersReducedMotion
                ? undefined
                : itemVariants
            }
            className={
              styles.copyrightArea
            }
          >
            <Stack gap={2}>
              <Text
                className={
                  styles.copyright
                }
              >
                © {currentYear}{" "}
                {companyName}. Todos os
                direitos reservados.
              </Text>

              <Group
                gap={5}
                justify="flex-end"
                className={
                  styles.loveGroup
                }
              >
                <Text
                  className={
                    styles.loveText
                  }
                >
                  {loveMessage}
                </Text>

                <Motion.span
                  className={
                    styles.heartWrapper
                  }
                  animate={
                    prefersReducedMotion
                      ? undefined
                      : {
                          scale: [
                            1,
                            1.15,
                            1,
                          ],
                        }
                  }
                  transition={{
                    duration: 2.2,
                    repeat: Infinity,
                    ease:
                      "easeInOut",
                  }}
                >
                  <FaHeart
                    className={
                      styles.heart
                    }
                    aria-label="Com carinho"
                  />
                </Motion.span>
              </Group>
            </Stack>
          </Motion.div>

          {/* Voltar ao topo */}

          <Tooltip
            label="Voltar ao início"
            withArrow
          >
            <Motion.div
              variants={
                prefersReducedMotion
                  ? undefined
                  : itemVariants
              }
              whileHover={
                prefersReducedMotion
                  ? undefined
                  : {
                      y: -3,
                      scale: 1.05,
                    }
              }
              whileTap={{
                scale: 0.95,
              }}
              transition={{
                duration: 0.4,
                ease: SMOOTH_EASE,
              }}
            >
              <ActionIcon
                type="button"
                size={48}
                radius="xl"
                variant="default"
                className={
                  styles.scrollTop
                }
                onClick={
                  handleScrollToTop
                }
                aria-label="Voltar ao início da página"
              >
                <FaArrowUp
                  aria-hidden="true"
                />
              </ActionIcon>
            </Motion.div>
          </Tooltip>
        </Box>
      </Container>
    </Motion.footer>
  );
}

export default memo(Footer);